/**
 * Defensive sort for the bootstrap `shops` array.
 *
 * The backend is the canonical source of ordering — see
 * bootstrap.service.ts, which sorts owner shops first, then by
 * (createdAt, name, id). This helper exists so a stale cache, an
 * alternate caller, or a future change to the wire shape cannot
 * regress callers that make a navigation decision off `shops[0]`.
 *
 * Frontend constraints:
 *   - BootstrapShop has no createdAt. We sort by what we have:
 *       role rank (OWNER → MANAGER → STAFF → RENTER → unknown)
 *       then name (case-insensitive ascending)
 *       then id (ascending).
 *   - The returned array is a new array; the input is not mutated.
 *
 * Only call this at navigation-decision boundaries. Display lists
 * already render whatever order the backend supplies.
 */

import type { BootstrapShop } from '@/shared/api/bootstrap';

const ROLE_RANK: Record<string, number> = {
  OWNER: 0,
  MANAGER: 1,
  STAFF: 2,
  RENTER: 3,
};

const rankOf = (role: string): number =>
  Object.prototype.hasOwnProperty.call(ROLE_RANK, role)
    ? ROLE_RANK[role]
    : 99;

export const sortBootstrapShops = (
  shops: readonly BootstrapShop[],
): BootstrapShop[] => {
  return [...shops].sort((a, b) => {
    const roleDiff = rankOf(a.role) - rankOf(b.role);
    if (roleDiff !== 0) return roleDiff;

    const nameDiff = a.name.toLocaleLowerCase().localeCompare(
      b.name.toLocaleLowerCase(),
    );
    if (nameDiff !== 0) return nameDiff;

    return a.id < b.id ? -1 : a.id > b.id ? 1 : 0;
  });
};
